import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, addDoc, query, where, getDocs, Timestamp, doc, getDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useEffectiveUser } from '../contexts/AuthContext';
import PieChartStructure from '../components/PieChartStructure';
import { CURRENCY_SYMBOLS, CURRENCY_SYMBOLS_TEXT } from '../utils/currencySymbols';
import { toast } from 'react-toastify';
import { FaChartPie, FaPlus, FaChartBar, FaList } from 'react-icons/fa';
import Header from '../components/Header';
import { format, startOfMonth, endOfMonth, isWithinInterval, subMonths, addMonths } from 'date-fns';
import DataTableStructure from '../components/DataTableStructure';
import IncomeModal from '../components/IncomeModal';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import DeleteModal from '../components/DeleteModal';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';

const IncomeTracking = () => {
  const { currentUser } = useEffectiveUser();
  const [incomes, setIncomes] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState(['GBP']); // Default to GBP
  const [totalIncome, setTotalIncome] = useState(0);
  const [tableFilter, setTableFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [categories, setCategories] = useState([
    'Salary',
    'Freelance',
    'Investments',
    'Business',
    'Rental',
    'Dividends',
    'Side Projects',
    'Other'
  ]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingIncome, setDeletingIncome] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterType, setFilterType] = useState('all'); // 'all', 'month', 'year'
  const [expenses, setExpenses] = useState({}); // Add this line
  
  // Table columns configuration
  const columns = React.useMemo(
    () => [
      {
        Header: 'Date',
        accessor: 'date',
        Cell: ({ value }) => format(value, 'MMM dd, yyyy')
      },
      {
        Header: 'Title',
        accessor: 'title'
      },
      {
        Header: 'Category',
        accessor: 'category'
      },
      {
        Header: 'Currency',
        accessor: 'currency'
      },
      {
        Header: 'Amount',
        accessor: 'amount',
        Cell: ({ value, row }) => (
          <span className="text-green-600 font-semibold">
            {CURRENCY_SYMBOLS[row.original.currency]}{value.toFixed(2)}
          </span>
        )
      },
      {
        Header: 'Actions',
        Cell: ({ row }) => (
          <div className="flex space-x-2">
            <button
              onClick={() => handleEdit(row.original)}
              className="p-1 text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              <FiEdit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDelete(row.original)}
              className="p-1 text-red-600 hover:text-red-800 transition-colors"
            >
              <FiTrash2 className="w-4 h-4" />
            </button>
          </div>
        )
      }
    ],
    []
  );

  // Calculate monthly stats
  const calculateMonthlyStats = () => {
    if (!incomes.length) return { average: 0, sources: 0 };
    
    // Group incomes by month-year
    const monthlyTotals = incomes.reduce((acc, income) => {
      const monthYear = format(income.date, 'yyyy-MM');
      acc[monthYear] = (acc[monthYear] || 0) + income.amount;
      return acc;
    }, {});

    // Calculate average across months that have data
    const monthlyAverage = Object.values(monthlyTotals).reduce((sum, total) => sum + total, 0) 
      / Object.keys(monthlyTotals).length;
    
    const uniqueSources = new Set(incomes.map(income => income.category)).size;
    
    return {
      average: monthlyAverage,
      sources: uniqueSources
    };
  };

  const monthlyStats = calculateMonthlyStats();

  // Calculate total income when incomes change
  useEffect(() => {
    const total = incomes.reduce((sum, income) => sum + income.amount, 0);
    setTotalIncome(total);
  }, [incomes]);

  // Fetch user settings and incomes
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user settings first to get available currencies
        const settingsDoc = await getDoc(doc(db, 'userSettings', currentUser.uid));
        if (settingsDoc.exists()) {
          const userSettings = settingsDoc.data();
          setAvailableCurrencies(userSettings.currencies || ['GBP']);
        }

        // Fetch income categories specifically
        const incomeCategoriesDoc = await getDoc(doc(db, 'incomeCategories', currentUser.uid));
        if (incomeCategoriesDoc.exists()) {
          setCategories(incomeCategoriesDoc.data().items || [
            'Salary',
            'Freelance',
            'Investments',
            'Business',
            'Rental',
            'Dividends',
            'Side Projects',
            'Other'
          ]);
        } else {
          // If no categories exist yet, create default ones
          await setDoc(doc(db, 'incomeCategories', currentUser.uid), {
            items: [
              'Salary',
              'Freelance',
              'Investments',
              'Business',
              'Rental',
              'Dividends',
              'Side Projects',
              'Other'
            ]
          });
        }

        // Fetch incomes
        const q = query(
          collection(db, 'incomes'),
          where('userId', '==', currentUser.uid)
        );
        const querySnapshot = await getDocs(q);
        const incomesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date.toDate()
        }));
        setIncomes(incomesData);

        // Fetch expenses
        const expensesRef = doc(db, 'expenses', currentUser.uid);
        const expensesDoc = await getDoc(expensesRef);
        if (expensesDoc.exists()) {
          setExpenses(expensesDoc.data().items || {});
        } else {
          setExpenses({});
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      }
    };

    fetchData();
  }, [currentUser]);

  // Handle form submission
  const handleSubmit = async (formData) => {
    try {
      // Validate required fields
      if (!formData.title || !formData.category || !formData.amount) {
        toast.error('Please fill in all required fields');
        return;
      }

      const incomeData = {
        userId: currentUser.uid,
        title: formData.title,
        category: formData.category, // This is now a string, not an object
        amount: parseFloat(formData.amount),
        description: formData.description || '', // Ensure description is never undefined
        currency: formData.currency,
        date: Timestamp.fromDate(new Date(formData.date)),
        ...(formData.id ? {} : { createdAt: Timestamp.now() })
      };

      if (formData.id) {
        await updateDoc(doc(db, 'incomes', formData.id), incomeData);
        setIncomes(prev => prev.map(income => 
          income.id === formData.id 
            ? { ...incomeData, id: formData.id, date: new Date(formData.date) }
            : income
        ));
        toast.success('Income updated successfully');
      } else {
        const docRef = await addDoc(collection(db, 'incomes'), incomeData);
        setIncomes(prev => [...prev, {
          ...incomeData,
          id: docRef.id,
          date: new Date(formData.date)
        }]);
        toast.success('Income added successfully');
      }

      setIsModalOpen(false);
      setEditingIncome(null);
    } catch (error) {
      console.error('Error saving income:', error);
      toast.error('Failed to save income');
    }
  };

  // Update this function to properly group by currency
  const prepareCategoryData = (incomeData) => {
    const categoryTotals = {};
    
    incomeData.forEach(income => {
      const category = income.category || 'Uncategorized';
      const currency = income.currency;
      
      if (!categoryTotals[currency]) {
        categoryTotals[currency] = {};
      }
      
      if (!categoryTotals[currency][category]) {
        categoryTotals[currency][category] = {
          name: category,
          value: 0,
          currency: currency
        };
      }
      
      categoryTotals[currency][category].value += parseFloat(income.amount);
    });

    // Convert the nested structure to array format
    return Object.values(categoryTotals).map(currencyGroup => 
      Object.values(currencyGroup).map(({ name, value, currency }) => ({
        name,
        value: Number(value.toFixed(2)),
        currency
      }))
    ).flat();
  };

  // Add the updateCategories function
  const updateCategories = async (newCategories) => {
    try {
      await setDoc(doc(db, 'incomeCategories', currentUser.uid), {
        items: newCategories
      });
      setCategories(newCategories);
      return true;
    } catch (error) {
      console.error('Error updating income categories:', error);
      toast.error('Failed to update categories');
      return false;
    }
  };

  // Add these functions to handle editing and updating
  const handleEdit = (income) => {
    setEditingIncome(income);
    setIsModalOpen(true);
  };

  // Update the handleDelete function
  const handleDelete = (income) => {
    setDeletingIncome(income);
    setIsDeleteModalOpen(true);
  };

  // Add new function to handle confirmation
  const handleConfirmDelete = async () => {
    try {
      await deleteDoc(doc(db, 'incomes', deletingIncome.id));
      setIncomes(prev => prev.filter(income => income.id !== deletingIncome.id));
      toast.success('Income deleted successfully');
    } catch (error) {
      console.error('Error deleting income:', error);
      toast.error('Failed to delete income');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingIncome(null);
    }
  };

  // Make sure to close the modal and reset editing state when closing
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingIncome(null);
  };

  // Add these helper functions
  const filterIncomesByDate = (incomes) => {
    if (filterType === 'all') return incomes;
    
    if (filterType === 'month') {
      const start = startOfMonth(selectedDate);
      const end = endOfMonth(selectedDate);
      
      return incomes.filter(income => 
        isWithinInterval(income.date, { start, end })
      );
    }
    
    if (filterType === 'year') {
      const year = selectedDate.getFullYear();
      return incomes.filter(income => 
        income.date.getFullYear() === year
      );
    }
    
    return incomes;
  };

  const navigateDate = (direction) => {
    setSelectedDate(current => {
      if (filterType === 'month') {
        return direction === 'prev' ? subMonths(current, 1) : addMonths(current, 1);
      } else if (filterType === 'year') {
        const newDate = new Date(current);
        newDate.setFullYear(current.getFullYear() + (direction === 'prev' ? -1 : 1));
        return newDate;
      }
      return current;
    });
  };

  // Filter incomes before passing to components
  const filteredIncomes = filterIncomesByDate(incomes);

  // Calculate total income for filtered data
  const calculateFilteredIncome = () => {
    return filteredIncomes.reduce((sum, income) => sum + income.amount, 0);
  };

  // Add function to calculate expenses for the selected month by currency
  const calculateMonthlyExpensesByCurrency = () => {
    if (filterType !== 'month') return {};
    
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    
    const totals = {};
    
    Object.entries(expenses).forEach(([date, dayExpenses]) => {
      const expenseDate = new Date(date);
      if (isWithinInterval(expenseDate, { start, end })) {
        dayExpenses.forEach(expense => {
          const currency = expense.currency;
          if (!totals[currency]) {
            totals[currency] = 0;
          }
          totals[currency] += parseFloat(expense.amount);
        });
      }
    });
    
    return totals;
  };

  // Calculate income by currency for the selected month
  const calculateMonthlyIncomeByCurrency = () => {
    const totals = {};
    
    filteredIncomes.forEach(income => {
      const currency = income.currency;
      if (!totals[currency]) {
        totals[currency] = 0;
      }
      totals[currency] += parseFloat(income.amount);
    });
    
    return totals;
  };

  // Update the period selector UI
  const DateFilterSelector = () => (
    <div className="flex flex-col sm:flex-row gap-4 sm:items-center bg-white dark:bg-gray-800 rounded-lg shadow p-2">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setFilterType('all')}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm ${
            filterType === 'all'
              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200'
              : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
          }`}
        >
          All Time
        </button>
        <button
          onClick={() => setFilterType('month')}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm ${
            filterType === 'month'
              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200'
              : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setFilterType('year')}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm ${
            filterType === 'year'
              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200'
              : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
          }`}
        >
          Yearly
        </button>
      </div>

      {/* Month/Year Selector */}
      {(filterType === 'month' || filterType === 'year') && (
        <div className="flex items-center gap-2 sm:border-l sm:pl-4 dark:border-gray-700">
          <button
            onClick={() => navigateDate('prev')}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <span className="flex-1 text-center text-sm font-medium min-w-[120px]">
            {filterType === 'month' 
              ? format(selectedDate, 'MMMM yyyy')
              : format(selectedDate, 'yyyy')}
          </span>
          <button
            onClick={() => navigateDate('next')}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );

  // Create a component for the period-specific income summary
  const PeriodIncomeSummary = () => {
    const filteredTotal = calculateFilteredIncome();
    const uniqueSources = new Set(filteredIncomes.map(income => income.category)).size;
    const monthlyExpenses = calculateMonthlyExpensesByCurrency();
    const monthlyIncome = calculateMonthlyIncomeByCurrency();
    
    // Different gradient colors for monthly and yearly views
    const gradientClass = filterType === 'month'
      ? "from-blue-700 to-cyan-800" // Blue gradient for monthly view
      : "from-amber-600 to-red-800"; // Amber/red gradient for yearly view
    
    const iconComponent = filterType === 'month'
      ? <FaChartBar className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
      : <FaChartPie className="w-12 h-12 sm:w-16 sm:h-16 text-white" />;
    
    return (
      <motion.div 
        className={`relative overflow-hidden bg-gradient-to-br ${gradientClass} rounded-2xl p-4 sm:p-6 shadow-lg w-full`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="relative z-10">
          <h3 className="text-xs sm:text-sm text-gray-100 font-medium">
            {filterType === 'month' 
              ? `${format(selectedDate, 'MMMM yyyy')} Income` 
              : `${format(selectedDate, 'yyyy')} Annual Income`}
          </h3>
          
          <div className="mt-3">
            {filteredIncomes.length > 0 ? (
              <>
                {/* Income Section - Compact */}
                <div className="space-y-0.5">
                  {Object.entries(monthlyIncome).map(([currency, amount]) => (
                    <p key={currency} className="text-2xl sm:text-3xl font-bold text-white">
                      {CURRENCY_SYMBOLS_TEXT[currency]}{amount.toFixed(2)}
                    </p>
                  ))}
                </div>
                <p className="text-xs sm:text-sm text-gray-100 mt-1">
                  From {uniqueSources} {uniqueSources === 1 ? 'category' : 'categories'}
                </p>
                
                {/* Show expense comparison for monthly view - Compact Grid Layout */}
                {filterType === 'month' && Object.keys(monthlyExpenses).length > 0 && (
                  <div className="mt-4 pt-3 border-t border-white/20">
                    <div className="grid grid-cols-2 gap-4">
                      {/* Expenses Column */}
                      <div>
                        <p className="text-xs text-gray-200 mb-1.5 font-medium uppercase tracking-wide">Expenses</p>
                        <div className="space-y-0.5">
                          {Object.entries(monthlyExpenses).map(([currency, amount]) => (
                            <p key={currency} className="text-base sm:text-lg font-semibold text-white/95">
                              {CURRENCY_SYMBOLS_TEXT[currency]}{amount.toFixed(2)}
                            </p>
                          ))}
                        </div>
                      </div>
                      
                      {/* Net Column */}
                      <div>
                        <p className="text-xs text-gray-200 mb-1.5 font-medium uppercase tracking-wide">Net</p>
                        <div className="space-y-0.5">
                          {Object.entries(monthlyIncome).map(([currency, incomeAmount]) => {
                            const expenseAmount = monthlyExpenses[currency] || 0;
                            const net = incomeAmount - expenseAmount;
                            return (
                              <p 
                                key={currency} 
                                className={`text-base sm:text-lg font-bold ${net >= 0 ? 'text-green-200' : 'text-red-200'}`}
                              >
                                {net >= 0 ? '+' : ''}{CURRENCY_SYMBOLS_TEXT[currency]}{net.toFixed(2)}
                              </p>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-lg sm:text-xl text-gray-100">No data available</p>
            )}
          </div>
        </div>
        <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 opacity-20">
          {iconComponent}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto py-6 px-4">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-2xl md:text-4xl min-h-12 font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
            Income Tracking
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Monitor your earnings and analyse income patterns across different sources
          </p>
        </motion.div>

        {/* Top Stats Cards */}
        {filterType === 'all' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <motion.div 
              className="relative overflow-hidden bg-gradient-to-br from-emerald-700 to-teal-800 rounded-2xl p-6 shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="relative z-10">
                <h3 className="text-sm text-emerald-100">Total Income</h3>
                <div className="mt-2">
                  {incomes.length > 0 ? (
                    <p className="text-3xl font-bold text-white">
                      {CURRENCY_SYMBOLS[availableCurrencies[0]]}{totalIncome.toFixed(2)}
                    </p>
                  ) : (
                    <p className="text-xl text-emerald-100">No data available</p>
                  )}
                </div>
              </div>
              <div className="absolute bottom-0 right-0 p-4 opacity-20">
                <FaChartPie className="w-16 h-16 text-white" />
              </div>
            </motion.div>

            <motion.div 
              className="relative overflow-hidden bg-gradient-to-br from-violet-700 to-purple-800 rounded-2xl p-6 shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="relative z-10">
                <h3 className="text-sm text-violet-100">Monthly Average</h3>
                <div className="mt-2">
                  {incomes.length > 0 ? (
                    <p className="text-3xl font-bold text-white">
                      {CURRENCY_SYMBOLS[availableCurrencies[0]]}
                      {monthlyStats.average.toFixed(2)}
                    </p>
                  ) : (
                    <p className="text-xl text-violet-100">No data available</p>
                  )}
                </div>
              </div>
              <div className="absolute bottom-0 right-0 p-4 opacity-20">
                <FaChartBar className="w-16 h-16 text-white" />
              </div>
            </motion.div>

            <motion.div 
              className="relative overflow-hidden bg-gradient-to-br from-amber-700 to-orange-800 rounded-2xl p-6 shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="relative z-10">
                <h3 className="text-sm text-amber-100">Income Sources</h3>
                <div className="mt-2">
                  {incomes.length > 0 ? (
                    <>
                      <p className="text-3xl font-bold text-white">
                        {monthlyStats.sources}
                      </p>
                      <p className="text-sm text-amber-100 mt-1">
                        Active Categories
                      </p>
                    </>
                  ) : (
                    <p className="text-xl text-amber-100">No data available</p>
                  )}
                </div>
              </div>
              <div className="absolute bottom-0 right-0 p-4 opacity-20">
                <FaList className="w-16 h-16 text-white" />
              </div>
            </motion.div>
          </div>
        ) : (
          <div className="mb-8">
            <PeriodIncomeSummary />
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <motion.button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex justify-center items-center bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaPlus className="w-4 h-4 mr-2" />
            <span>Add Income</span>
          </motion.button>
          <DateFilterSelector />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Charts Section - Takes 2 columns */}
          <div className="lg:col-span-2">
            <motion.div 
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Income Distribution
              </h3>
              <PieChartStructure
                data={prepareCategoryData(filteredIncomes)}
                title="Income Distribution"
              />
            </motion.div>
          </div>

          {/* Recent Activity - Takes 1 column */}
          <div className="lg:col-span-1">
            <motion.div 
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Recent Income
              </h3>
              <div className="space-y-4">
                {incomes
                  .sort((a, b) => b.date - a.date) // Sort by date descending (newest first)
                  .slice(0, 4) // Show only 4 most recent incomes
                  .map(income => (
                  <div 
                    key={income.id}
                    className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-700 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{income.category}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {format(income.date, 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <p className="text-green-600 font-semibold">
                      {CURRENCY_SYMBOLS[income.currency]}{income.amount.toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Full Width Data Table */}
          <motion.div 
            className="lg:col-span-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Income Records
              </h3>
              <DataTableStructure
                columns={columns}
                data={filteredIncomes}
                initialPageSize={10}
                globalFilter={tableFilter}
                setGlobalFilter={setTableFilter}
                initialSortBy={[{ id: 'date', desc: false }]}
              />
            </div>
          </motion.div>
        </div>

        {/* Income Modal */}
        {isModalOpen && (
          <IncomeModal
            onClose={handleCloseModal}
            onSubmit={handleSubmit}
            categories={categories}
            updateCategories={updateCategories}
            currencies={availableCurrencies}
            editingIncome={editingIncome}
            selectedDate={new Date()}
          />
        )}

        {/* Add DeleteModal */}
        <DeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setDeletingIncome(null);
          }}
          onConfirm={handleConfirmDelete}
          itemName={deletingIncome?.title || 'this income'}
        />
      </div>
    </div>
  );
};

export default IncomeTracking;
